<template>
  <component
    :is="href ? 'a' : 'button'"
    :href="resolvedHref"
    :type="href ? undefined : (submit ? 'submit' : 'button')"
    class="btn"
    :class="buttonClasses"
    :style="buttonStyle"
    @click="href ? onLinkClick($event) : undefined"
  >
    <!-- primary-arrow: градиент + иконка стрелки + анимация | secondary-arrow: белый фон + иконка стрелки + анимация -->
    <template v-if="variant === 'primary-arrow' || variant === 'secondary-arrow'">
      <span class="btn-arrow-wrap">
        <span class="btn-text">
          <template v-if="text != null">{{ text }}</template>
          <slot v-else />
        </span>
        <span class="btn-icon">
          <svg class="btn-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <defs>
              <linearGradient id="btn-arrow-gradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#09AFEA" />
                <stop offset="52%" stop-color="#19E089" />
                <stop offset="100%" stop-color="#19E089" />
              </linearGradient>
            </defs>
            <line v-if="arrowDirection === 'right'" x1="5" y1="12" x2="19" y2="12" />
            <line v-if="arrowDirection === 'left'" x1="19" y1="12" x2="5" y2="12" />
            <polyline v-if="arrowDirection === 'right'" points="12 5 19 12 12 19" />
            <polyline v-else points="12 5 5 12 12 19" />
          </svg>
        </span>
        <span class="btn-icon">
          <svg class="btn-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <defs>
              <linearGradient id="btn-arrow-gradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#09AFEA" />
                <stop offset="52%" stop-color="#19E089" />
                <stop offset="100%" stop-color="#19E089" />
              </linearGradient>
            </defs>
            <line v-if="arrowDirection === 'right'" x1="5" y1="12" x2="19" y2="12" />
            <line v-if="arrowDirection === 'left'" x1="19" y1="12" x2="5" y2="12" />
            <polyline v-if="arrowDirection === 'right'" points="12 5 19 12 12 19" />
            <polyline v-else points="12 5 5 12 12 19" />
          </svg>
        </span>
      </span>
    </template>
    <!-- primary: градиент без иконки | secondary: белый фон -->
    <template v-else>
      <template v-if="text != null">{{ text }}</template>
      <slot v-else />
    </template>
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const router = useRouter()

interface Props {
  /** primary-arrow — градиент + иконка стрелки, primary — без иконки, secondary — белый фон, secondary-arrow — белый фон + иконка стрелки, error — червоний градієнт для помилок */
  variant?: 'primary-arrow' | 'primary' | 'secondary' | 'secondary-arrow' | 'error'
  /** Ссылка: строка пути или объект маршрута (path, name + params) для NuxtLink */
  href?: RouteLocationRaw
  /** Текст кнопки (альтернатива слоту) */
  text?: string
  /** В формах: type="submit" (для любого варианта) */
  submit?: boolean
  /** Кастомный паддинг (переопределяет размер из варианта), напр. "12px 24px", "1rem 2rem" */
  padding?: string
  /** Кастомный радиус скругления: строка "8px", "50%", или число (будет в px) */
  rounded?: string | number
  /** Размер: для secondary по умолчанию small; можно задать large */
  size?: 'small' | 'large'
  /** Направление стрелки для primary-arrow / secondary-arrow: вправо (по умолчанию) или влево */
  arrowDirection?: 'left' | 'right'
  /** Показывать обводку (border). false — без обводки */
  outline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  href: undefined,
  text: undefined,
  submit: false,
  padding: undefined,
  rounded: undefined,
  size: undefined,
  arrowDirection: 'right',
  outline: true
})

const resolvedHref = computed(() => {
  if (!props.href) return '#'
  if (typeof props.href === 'string') return props.href
  return router.resolve(props.href).href
})

function onLinkClick (e: MouseEvent) {
  if (!props.href) return
  const url = typeof props.href === 'string' ? props.href : resolvedHref.value
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) return
  e.preventDefault()
  navigateTo(props.href)
}

const buttonClasses = computed(() => {
  const base = []
  if (props.arrowDirection === 'left') {
    base.push('btn-arrow-left')
  }
  if (props.outline === false) {
    base.push('btn-no-outline')
  }
  if (props.variant === 'primary-arrow') {
    base.push(
      'btn-gradient-emerald-blue-emerald-green',
      'btn-hover-animation-switch'
    )
    if (!props.padding) base.push('btn-large')
    if (!props.rounded) base.push('btn-round-edge')
  } else if (props.variant === 'primary') {
    base.push('btn-gradient-emerald-blue-emerald-green')
    if (!props.padding) base.push('btn-large')
    if (!props.rounded) base.push('btn-round-edge')
  } else if (props.variant === 'secondary-arrow') {
    base.push('btn-secondary-white', 'submit', 'btn-hover-animation-switch')
    if (!props.padding) {
      base.push(props.size === 'large' ? 'btn-large' : 'btn-small')
    }
    if (!props.rounded) base.push('btn-round-edge')
  } else if (props.variant === 'secondary') {
    base.push('btn-secondary-white', 'submit')
    if (!props.padding) {
      base.push(props.size === 'large' ? 'btn-large' : 'btn-small')
    }
    if (!props.rounded) base.push('btn-round-edge')
  } else if (props.variant === 'error') {
    base.push('btn-gradient-error')
    if (!props.padding) base.push('btn-large')
    if (!props.rounded) base.push('btn-round-edge')
  }
  return base
})

const buttonStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.padding) style.padding = props.padding
  if (props.rounded !== undefined && props.rounded !== null) {
    const r = props.rounded
    if (typeof r === 'number') {
      style.borderRadius = `${r}px`
    } else {
      const num = parseFloat(r)
      style.borderRadius = Number.isNaN(num) ? r : `${num}px`
    }
  }
  return Object.keys(style).length ? style : undefined
})
</script>

<style scoped>
/* Базовые стили из ui-kit.css; здесь только переопределения при необходимости */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
  font-family: var(--font-inter);
  font-weight: 400;
  user-select: none;
}

/* Secondary-hover (як у макеті): градієнтний текст + градієнтна стрілка */
.btn.btn-secondary-white.btn-hover-animation-switch:hover .btn-arrow line,
.btn.btn-secondary-white.btn-hover-animation-switch:hover .btn-arrow polyline {
  stroke: #19E089;
}

.btn.btn-secondary-white.btn-hover-animation-switch:hover .btn-text {
  background-image: linear-gradient(to right, #09AFEA, #19E089, #19E089);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Без обводки */
.btn.btn-no-outline {
  border: none !important;
}

.btn.btn-no-outline:hover {
  border: none !important;
}

/* Error variant styles - точний стиль з макету */
.btn-gradient-error {
  display: inline-block;
  padding: 6px 28px;
  font-size: 17px;
  font-family: var(--font-inter);
  font-weight: 100;
  text-align: center;
  color: #FFFFFF;
  background-color: transparent;
  border: 2px solid transparent;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
  user-select: none;
  background-image: linear-gradient(to right, #FF6B6B, #FF3B66, #FF6B6B);
  background-size: 200%;
  background-position: left center;
  transition: background-position 0.4s ease-in-out, transform 0.3s cubic-bezier(0.2, 0, 0.3, 1);
}

.btn-gradient-error:hover {
  background-position: right center;
  transform: translate3d(0, -2px, 0);
  color: #FFFFFF;
}

/* Стрелка влево: иконка слева от текста, анимация при hover в обратную сторону */
.btn-arrow-left .btn-arrow-wrap {
  flex-direction: row-reverse;
}

/* Порядок: видимая иконка слева, текст, вторая иконка справа (появляется при hover) */
.btn-arrow-left .btn-text {
  order: 1;
  margin-left: 6px;
  margin-right: -13px;
  transition: transform 0.6s cubic-bezier(0.1, 0.75, 0.25, 1) 125ms;
}

.btn-arrow-left .btn-icon:nth-child(2) {
  order: 2;
  margin-left: -13px;
  margin-right: 6px;
  transition: opacity 0.4s cubic-bezier(0.1, 0.75, 0.25, 1) 0.25s, transform 0.6s cubic-bezier(0.1, 0.75, 0.25, 1) 0.25s;
}

.btn-arrow-left .btn-icon + .btn-icon {
  order: -2;
  margin-left: 0;
  margin-right: 0;
  opacity: 0;
  transform: translateX(10px);
  transition: opacity 0.4s cubic-bezier(0.1, 0.75, 0.25, 1) 0.25s, transform 0.6s cubic-bezier(0.1, 0.75, 0.25, 1) 0.25s;
  transition-delay: 0s;
}

.btn-arrow-left.btn-hover-animation-switch:hover .btn-text {
  transform: translateX(-23px);
  transition-delay: 0.1s;
}

.btn-arrow-left.btn-hover-animation-switch:hover .btn-icon:nth-child(2) {
  transform: translateX(-10px);
  opacity: 0;
  transition-delay: 0s;
}

.btn-arrow-left.btn-hover-animation-switch:hover .btn-icon + .btn-icon {
  opacity: 1;
  transform: translateX(0);
  transition-delay: 225ms;
}
@media (max-width: 768px) {
  .btn-arrow-left, .btn-primary-arrow {
    justify-content: start;
  }
  a.btn.btn-arrow-left.btn-no-outline.btn-secondary-white.submit.btn-hover-animation-switch{
    padding: 18px 30px!important;
  }
}
</style>
